import {
  Avatar,
  EditIcon,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  NavigationLink,
  PageDataList,
  PageDataListItem,
  PageHeader,
  PageSection,
  PageSectionHeader,
  PageSectionTitle,
  PageTitle,
  Typography,
} from '@acceso/design-system';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { EmptyStateErrorUnexpected } from '@/components/EmptyState/EmptyStateErrorUnexpected';
import { UserAction } from '@/components/UserAction';
import { useMe } from '@/features/users/api/get-me';

import { useUser } from '../../api/get-user';
import { ProfileDrawerForm } from '../../components/ProfileDrawerForm/ProfileDrawerForm';
import { UserPageSkeleton } from '../../components/UserPageSkeleton/UserPageSkeleton';
import { useProfileControllers } from '../../hooks/use-profile-controllers';

export default function ProfilePage() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { data: me } = useMe();

  const { data: user = null, isLoading, isError } = useUser({ id: me?.id });

  const { isMutating, drawer, handleChangePassword, handleChangeEmail, handleSubmit } =
    useProfileControllers();

  return (
    <>
      <PageHeader>
        <div className="space-y-2">
          <NavigationLink onClick={() => navigate(-1)}>
            <Typography fontSize={FontSizeEnum.Md} weight={FontWeightEnum.Semibold}>
              {t('Volver')}
            </Typography>
          </NavigationLink>
          <PageTitle>
            {user ? `${user.first_name} ${user.last_name || ''}` : t('Cargando...')}
          </PageTitle>
        </div>
      </PageHeader>

      <PageSection>
        {isLoading ? (
          <UserPageSkeleton />
        ) : isError ? (
          <EmptyStateErrorUnexpected />
        ) : user ? (
          <>
            <PageSectionHeader>
              <PageSectionTitle>{t('Datos generales')}</PageSectionTitle>

              <div className="flex items-center">
                <Typography
                  color={FontColorEnum.Dark10}
                  fontSize={FontSizeEnum.Lg}
                  weight={FontWeightEnum.Medium}
                >
                  {t('Editar')}
                </Typography>

                <IconButton
                  size={IconButtonSizeEnum.LG}
                  onClick={() => drawer.open(user)}
                  color={IconButtonColorEnum.NONE_DEFAULT}
                >
                  <EditIcon size={IconSizeEnum.LG} className="p-[2px]" />
                </IconButton>
              </div>
            </PageSectionHeader>
            <PageDataList>
              <PageDataListItem label={t('Nombre')}>
                <div className="flex items-center space-x-2">
                  <Avatar
                    user={{
                      first_name: user.first_name,
                      last_name: user.last_name || undefined,
                      avatar: null,
                    }}
                  />
                  <Typography weight={FontWeightEnum.Semibold}>
                    {user.first_name} {user.last_name}
                  </Typography>
                </div>
              </PageDataListItem>

              <PageDataListItem label={t('Correo electrónico')}>
                <Typography weight={FontWeightEnum.Bold}>{user.email}</Typography>
              </PageDataListItem>

              <PageDataListItem label={t('Nombre de usuario')}>
                <Typography weight={FontWeightEnum.Bold}>{user.username}</Typography>
              </PageDataListItem>

              <PageDataListItem label={t('Creado por')}>
                <UserAction id={user.created_by} timestamp={user.created_at} />
              </PageDataListItem>
              <PageDataListItem label={t('Actualizado por')}>
                {user.updated_at ? (
                  <UserAction id={user.updated_by} timestamp={user.updated_at} />
                ) : null}
              </PageDataListItem>
            </PageDataList>
          </>
        ) : null}
      </PageSection>

      <ProfileDrawerForm
        isOpen={drawer.isOpen}
        isMutating={isMutating}
        data={drawer.data}
        onChangePassword={handleChangePassword}
        onChangeEmail={handleChangeEmail}
        onCancel={drawer.close}
        onSubmit={handleSubmit}
      />
    </>
  );
}
